// The platform-wide error code catalog. Every API response on every zone
// uses the same envelope:
//   { error: { code, message, details? } | null, data: T | null }
// `code` is SCREAMING_SNAKE and machine-readable — the UI branches on it, never
// on `message`. Every code maps to exactly one `ErrorClass`, and every class has
// exactly one American-English copy sentence, defined once here and never
// inlined at a call site (rules.md "Error Handling").
//
// This file seeds the codes @sportsmet/api-core itself throws or returns. A
// service adding a new domain-specific code (e.g. contest-api's
// `ALREADY_SUBMITTED`) extends `ERROR_CODES` and `errorCodeClass` in the same
// change — the completeness test in tests/errorCodes.test.ts fails the build if
// a code is ever added without a class.

export const ERROR_CODES = [
  // validation
  "VALIDATION_ERROR",
  // The observability destination is not a URL the collector can export to. Distinct from VALIDATION_ERROR so the staff screen can name the exact problem next to the field.
  "OBSERVABILITY_ENDPOINT_INVALID",
  "EMAIL_ADDRESS_REFUSED",
  // The SMS provider rejected the number itself: not a real line, not able to
  // take a text, or not in a country the account may write to. The email twin
  // is EMAIL_ADDRESS_REFUSED directly above, and it classes the same way,
  // because in both the caller gave us something we cannot deliver to.
  "SMS_NUMBER_REFUSED",
  // Over the 5,000-code-point message cap. Distinct from VALIDATION_ERROR so the composer can show a character count rather than a generic message.
  "MESSAGE_TOO_LONG",
  // You cannot leave a pool's chat or a private chat, you mute it. Named rather than a silent no-op so the app can explain why.
  "CHAT_LEAVE_NOT_ALLOWED",
  // The caller is a stand-alone chat's only active Admin.
  // Distinct from CHAT_LEAVE_NOT_ALLOWED because the fix here is promoting someone else, not muting.
  "LAST_ADMIN_CANNOT_LEAVE",
  // An uploaded original's declared or measured byte size is over media-api's 15 MB cap.
  "FILE_TOO_LARGE",
  // A declared upload content type is outside media-api's four-value allowlist.
  "UNSUPPORTED_CONTENT_TYPE",
  // The uploaded bytes cannot be decoded as a valid image of a supported format, corrupt or truncated.
  "CORRUPT_IMAGE_FILE",
  // A reorder list did not name every visible Quick start for the league. Rejected whole: a partial reorder would leave gaps in the staff-chosen order.
  "INCOMPLETE_ORDERING",
  // The supplied string is not a recognized IANA time zone name. Distinct from
  // VALIDATION_ERROR so the Staff panel can point at the timezone field specifically.
  "INVALID_TIMEZONE",
  // A picked game has no line yet and acceptsPendingSpread was not sent true (SUR-R75). The consent is per pick, never once per account.
  "NEEDS_SPREAD_CONSENT",
  // notFound
  "NOT_FOUND",
  // The join code matches no pool, or its pool is closed. Distinct from
  // POOL_CODE_EXPIRED so the app can say 'check the code' rather than 'ask for a new one'.
  "POOL_CODE_NOT_FOUND",
  // The join code matched a grant the commissioner has since regenerated.
  // notFound, not conflict: the old code is simply gone.
  "POOL_CODE_EXPIRED",
  // No such message, or it is deleted, or the caller is not in its chat. One code for all three, for the same reason as FORBIDDEN_CHAT.
  "MESSAGE_NOT_FOUND",
  // No preset row with that presetId. Staff panel only, a fan never supplies a presetId to this zone.
  "NOTIFICATION_PRESET_NOT_FOUND",
  // No mediaObject matches the id, or it exists but is not owned by the caller. One code for both, so guessing a mediaId tells nothing.
  "MEDIA_NOT_FOUND",
  // No such Quick start, or staff already removed it. Removed is treated as gone, so a second remove answers the same way as a bad id.
  "QUICK_START_NOT_FOUND",
  // forbidden — auth pipeline (registerAuthMiddleware, api-design.md steps 1-5)
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "FORBIDDEN_POOL",
  "FORBIDDEN_ROLE",
  // The caller is not an active member of the target chat, or the chat does not exist. One code for both, so guessing chat ids tells an attacker nothing.
  "FORBIDDEN_CHAT",
  // The selected GIF's content rating is above messaging.gifContentRatingCeiling, or its rating could not be read. media-api's /gif/select refuses it before any bytes are fetched or stored.
  "GIF_RATING_NOT_ALLOWED",
  // The chat is set to officers-only and the caller is a plain member. Reacting is still allowed, which is why this gates /message/send and not the whole chat.
  "ANNOUNCEMENTS_ONLY",
  // A Commissioner or Admin timed this member out of posting in the chat (ISSUE-1364). Reacting still works. Distinct from ANNOUNCEMENTS_ONLY so the app can say until when.
  "CHAT_TIMED_OUT",
  // Staff switched chat off for this whole account from the Staff panel (ISSUE-1368). Every chat, until staff switch it back on. Distinct from CHAT_TIMED_OUT, which is one chat and runs out on its own.
  "CHAT_SUSPENDED",
  // The caller is in the chat and can see the message but did not write it. Safe to distinguish: membership is already proven.
  "NOT_MESSAGE_OWNER",
  // One code for every way a Google or Apple identity token can fail. Naming
  // the failed check would hand a forger an oracle to tune against, so the
  // discriminator lives in `details.reason` for logs and support only.
  "OAUTH_TOKEN_REJECTED",
  // Staff put this account on hold (ISSUE-790). Staff can lift it.
  "ACCOUNT_SUSPENDED",
  // Staff closed this account for good (ISSUE-790). Nobody can lift it.
  "ACCOUNT_BANNED",
  // conflict
  "CONFLICT",
  // A presigned upload URL's storage key has no bytes behind it yet, or has zero bytes. The client must PUT before calling complete.
  "UPLOAD_NOT_RECEIVED",
  // Refuses removing the platform's only remaining Global admin, because
  // nothing could grant it back.
  "LAST_SUPER_ADMIN",
  // Refuses removing the account's last remaining sign-in method (email,
  // Apple, or Google), because nothing could sign the fan back in afterward
  // (ISSUE-113/114, owner Q181).
  "LAST_SIGN_IN_METHOD",
  // A pool already has 5 custom award types of this kind (medal or trophy), the cap is on distinct types, not grants.
  "AWARD_TYPE_CAP_REACHED",
  // conflict, a moderation-queue mediaId was already resolved, or was never
  // pendingReview/heldOnBudget in the first place. ISSUE-288.
  "MEDIA_NOT_IN_QUEUE",
  // A contest from the prior season has not reached "confirmed"
  // yet (SUR-R6 gate b). contest-api's /season/start.
  "UNSETTLED_CONTEST_EXISTS",
  // A Commissioner or Admin already sent this player their one manual pick
  // nudge for this round. Capped per player, never per presser (Q244).
  // contest-api's /round/nudge-entry.
  "NUDGE_ALREADY_SENT",
  // Two contests in one pool cannot claim the same calendar day. Enforced by contestDayClaims' unique index, not by a read-then-write.
  "CONTEST_DATES_OVERLAP",
  // SUR-24: the caller joined the pool after this contest's start date, so they have no entry eligibility in it. Never means "another contest is running", SUR-34 allows that.
  "CONTEST_IN_PROGRESS",
  // The contest is confirmed and frozen forever (SUR-R61). A canceled-outcome contest is still playing and still accepts updates.
  "CONTEST_ALREADY_ENDED",
  // The member already holds maxEntriesPerMember entries in this contest (SUR-R51/SUR-R52). conflict, not validation: the request was well formed and the state refused it.
  "MAX_ENTRIES_REACHED",
  // The contest's first round has locked, so no member may start another entry (SUR-R39, owner answer Q-1043-A). conflict, not validation: the request was well formed and the state refused it.
  "ENTRY_WINDOW_CLOSED",
  // The team is inside its re-use lockout window, or its finite re-use count is spent (SUR-R25/SUR-R26). conflict, not validation: the request was well formed and the state refused it.
  "TEAM_NOT_SELECTABLE",
  // A skip only ever covers a round with zero picks; this entry already has one (SUR-R87/Q163). conflict, not validation: the request was well formed and the state refused it.
  "PICKS_ALREADY_SUBMITTED",
  // The entry's skip allowance for this contest is spent, or the pool allows no skips at all (SUR-R35). conflict, not validation: the request was well formed and the state refused it.
  "NO_SKIPS_LEFT",
  // The pool is at maxMembers. Distinct from CONFLICT so the join screen can
  // show this pool is full rather than a generic collision.
  "POOL_FULL",
  // ISSUE-1258. The pool type is on offer but its contest engine has not shipped, so /contest/bootstrap-for-pool refuses it. conflict: the request was well formed and the platform's state refused it.
  "CONTEST_TYPE_NOT_READY",
  // Squares (ISSUE-1262). Someone else took this square first. The client refetches the grid.
  "SQUARE_TAKEN",
  // Squares (ISSUE-1262). The member already holds the grid's max squares per member.
  "MAX_SQUARES_REACHED",
  // Squares (ISSUE-1265). Size, axes and checkpoints freeze at the season's first lock.
  "SQUARES_SETTING_FROZEN",
  // Squares (ISSUE-1265). Start a new season waits until every published grid is final or void.
  "SEASON_NOT_FINISHED",
  // Strip Card (ISSUE-1285). Someone else took this row first. The client refetches the card.
  "STRIP_SPOT_TAKEN",
  // Strip Card (ISSUE-1285). The member already holds the pool's max spots on this strip.
  "MAX_STRIP_SPOTS_REACHED",
  // Strip Card (ISSUE-1285). The card already has four strips.
  "STRIP_LIMIT_REACHED",
  // Strip Card (ISSUE-1291). Winners and unlock need a locked strip.
  "STRIP_NOT_LOCKED",
  // Pick'em (ISSUE-1302). Two picks in one round cannot share a confidence rank. The client refetches the round.
  "CONFIDENCE_RANK_TAKEN",
  // Pick'em (ISSUE-1306). Scoring format and pick basis freeze once the contest's first round opens.
  "PICKEM_SETTING_FROZEN",
  // lockedOut
  "LOCKED_OUT",
  // The round's pick deadline has passed (SUR-R21). Nothing the client can resend fixes it.
  "ROUND_LOCKED",
  // Squares (ISSUE-1262, ISSUE-1263). The grid is locked, live, final or void, so its squares cannot change.
  "GRID_LOCKED",
  // Squares (ISSUE-1263). The grid's game has started or been scored: no unpublish, no randomize.
  "GRID_STARTED",
  // Strip Card (ISSUE-1285, ISSUE-1290). The strip is locked, so its rows cannot change until an admin unlocks it.
  "STRIP_LOCKED",
  // Strip Card (ISSUE-1297). The card's game will not be played, so the board is read-only.
  "STRIP_GAME_CANCELED",
  // This one pick's own game has started, or the round locked, whichever came first (SUR-R21).
  "PICK_LOCKED",
  // Pick'em (ISSUE-1309). The tiebreaker game has started, so the total points guess is final.
  "TIEBREAKER_LOCKED",
  // A fan tried to toggle a channel notification-api forces to a locked
  // value (messageRequest phone/email, poolInvite in-app). NOTIF-10/Q53, MSG-19/Q96.
  "ALERT_CHANNEL_LOCKED",
  // entitlementRequired
  "ENTITLEMENT_REQUIRED",
  // rateLimited
  "RATE_LIMITED",
  // This account has already sent notification.smsDailySendCapPerUser texts in
  // the last 24 hours. A ceiling, not a refusal of the request itself, so it
  // classes as rateLimited rather than conflict.
  "SMS_CAP_REACHED",
  // providerUnavailable
  "PROVIDER_UNAVAILABLE",
  // The sports data provider has not published the
  // target season yet (SUR-R6 gate a). contest-api's /season/start-options and /season/start.
  "PROVIDER_SEASON_DATA_MISSING",
  // Nobody has set that provider's client ids yet, or config-api could not be
  // reached. To a fan both mean the same thing: the button is not working now.
  "OAUTH_PROVIDER_NOT_CONFIGURED",
  "EMAIL_NOT_CONFIGURED",
  "EMAIL_SEND_FAILED",
  // Nobody has saved an SMS provider name, key and from-number yet, so
  // notification-api has nothing to send a text through. This is the state the
  // platform ships in: the transport is built and switched off.
  "SMS_NOT_CONFIGURED",
  // The SMS provider was reachable and refused the send, or the call to it
  // failed outright. The email twin is EMAIL_SEND_FAILED directly above.
  "SMS_SEND_FAILED",
  // Nobody has saved the GoalServe API key yet, so goalserve-ingest-api has
  // nothing to call the vendor with. Named for the provider, like the OAuth
  // and email codes above, because a bare KEY_NOT_CONFIGURED in a catalog
  // shared by fifteen services means nothing on its own.
  "GOALSERVE_KEY_NOT_CONFIGURED",
  // The GIF provider's quota is exhausted, its API is down, or nobody has saved its API key yet. media-api's /gif/select.
  "GIF_PROVIDER_UNAVAILABLE",
  // providerUnavailable — a capability an operator has not switched on yet
  "CONTACT_FORM_DISABLED",
  // internal
  "INTERNAL_CALL_FAILED",
  "INTERNAL_ERROR",
  // The pool membership authority (pool-api) did not answer, so whether the
  // caller is a member of the pool is UNKNOWN. Deliberately not a forbidden
  // code: a caller who is refused with FORBIDDEN_POOL has been proven to lack
  // access, and folding an unreachable authority into that refusal is what
  // makes a pool-api outage read as "you are not in this pool". Carries no
  // poolId and no detail, so it is not an enumeration oracle: a real pool id
  // and a fabricated one answer identically.
  "POOL_MEMBERSHIP_UNAVAILABLE",
  // botCheckFailed
  "CAPTCHA_FAILED",
  // offline
  // The request never reached a server. The generated SDK call helper
  // synthesizes this in its transport catch path; no service ever returns it.
  "NETWORK_UNREACHABLE",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

// The error classes. Deliberately
// no "unauthenticated" class of its own — an unauthenticated caller is refused
// access, same as a caller who IS authenticated but lacks permission, so
// UNAUTHENTICATED lives under `forbidden` alongside FORBIDDEN / FORBIDDEN_POOL.
export type ErrorClass =
  | "notFound"
  | "validation"
  | "forbidden"
  | "conflict"
  | "lockedOut"
  | "entitlementRequired"
  | "rateLimited"
  | "providerUnavailable"
  | "internal"
  | "botCheckFailed"
  | "offline";

export const errorCodeClass = {
  VALIDATION_ERROR: "validation",
  OBSERVABILITY_ENDPOINT_INVALID: "validation",
  // The fan typed an address the receiving mail server refuses. It is a
  // validation code on purpose: the only useful next step is to check it.
  EMAIL_ADDRESS_REFUSED: "validation",
  SMS_NUMBER_REFUSED: "validation",
  MESSAGE_TOO_LONG: "validation",
  CHAT_LEAVE_NOT_ALLOWED: "validation",
  LAST_ADMIN_CANNOT_LEAVE: "validation",
  FILE_TOO_LARGE: "validation",
  UNSUPPORTED_CONTENT_TYPE: "validation",
  CORRUPT_IMAGE_FILE: "validation",
  INCOMPLETE_ORDERING: "validation",
  INVALID_TIMEZONE: "validation",
  NEEDS_SPREAD_CONSENT: "validation",
  NOT_FOUND: "notFound",
  POOL_CODE_NOT_FOUND: "notFound",
  POOL_CODE_EXPIRED: "notFound",
  MESSAGE_NOT_FOUND: "notFound",
  NOTIFICATION_PRESET_NOT_FOUND: "notFound",
  MEDIA_NOT_FOUND: "notFound",
  QUICK_START_NOT_FOUND: "notFound",
  UNAUTHENTICATED: "forbidden",
  FORBIDDEN: "forbidden",
  FORBIDDEN_POOL: "forbidden",
  FORBIDDEN_ROLE: "forbidden",
  FORBIDDEN_CHAT: "forbidden",
  GIF_RATING_NOT_ALLOWED: "forbidden",
  ANNOUNCEMENTS_ONLY: "forbidden",
  CHAT_TIMED_OUT: "forbidden",
  CHAT_SUSPENDED: "forbidden",
  NOT_MESSAGE_OWNER: "forbidden",
  OAUTH_TOKEN_REJECTED: "forbidden",
  ACCOUNT_SUSPENDED: "forbidden",
  ACCOUNT_BANNED: "forbidden",
  CONFLICT: "conflict",
  UPLOAD_NOT_RECEIVED: "conflict",
  LAST_SUPER_ADMIN: "conflict",
  LAST_SIGN_IN_METHOD: "conflict",
  AWARD_TYPE_CAP_REACHED: "conflict",
  MEDIA_NOT_IN_QUEUE: "conflict",
  UNSETTLED_CONTEST_EXISTS: "conflict",
  NUDGE_ALREADY_SENT: "conflict",
  CONTEST_DATES_OVERLAP: "conflict",
  CONTEST_IN_PROGRESS: "conflict",
  CONTEST_ALREADY_ENDED: "conflict",
  MAX_ENTRIES_REACHED: "conflict",
  ENTRY_WINDOW_CLOSED: "conflict",
  TEAM_NOT_SELECTABLE: "conflict",
  PICKS_ALREADY_SUBMITTED: "conflict",
  NO_SKIPS_LEFT: "conflict",
  POOL_FULL: "conflict",
  CONTEST_TYPE_NOT_READY: "conflict",
  SQUARE_TAKEN: "conflict",
  MAX_SQUARES_REACHED: "conflict",
  SQUARES_SETTING_FROZEN: "conflict",
  SEASON_NOT_FINISHED: "conflict",
  STRIP_SPOT_TAKEN: "conflict",
  MAX_STRIP_SPOTS_REACHED: "conflict",
  STRIP_LIMIT_REACHED: "conflict",
  STRIP_NOT_LOCKED: "conflict",
  CONFIDENCE_RANK_TAKEN: "conflict",
  PICKEM_SETTING_FROZEN: "conflict",
  LOCKED_OUT: "lockedOut",
  ROUND_LOCKED: "lockedOut",
  GRID_LOCKED: "lockedOut",
  GRID_STARTED: "lockedOut",
  STRIP_LOCKED: "lockedOut",
  STRIP_GAME_CANCELED: "lockedOut",
  PICK_LOCKED: "lockedOut",
  TIEBREAKER_LOCKED: "lockedOut",
  ALERT_CHANNEL_LOCKED: "lockedOut",
  ENTITLEMENT_REQUIRED: "entitlementRequired",
  RATE_LIMITED: "rateLimited",
  SMS_CAP_REACHED: "rateLimited",
  PROVIDER_UNAVAILABLE: "providerUnavailable",
  PROVIDER_SEASON_DATA_MISSING: "providerUnavailable",
  OAUTH_PROVIDER_NOT_CONFIGURED: "providerUnavailable",
  EMAIL_NOT_CONFIGURED: "providerUnavailable",
  EMAIL_SEND_FAILED: "providerUnavailable",
  SMS_NOT_CONFIGURED: "providerUnavailable",
  SMS_SEND_FAILED: "providerUnavailable",
  GOALSERVE_KEY_NOT_CONFIGURED: "providerUnavailable",
  GIF_PROVIDER_UNAVAILABLE: "providerUnavailable",
  CONTACT_FORM_DISABLED: "providerUnavailable",
  INTERNAL_CALL_FAILED: "internal",
  INTERNAL_ERROR: "internal",
  POOL_MEMBERSHIP_UNAVAILABLE: "internal",
  CAPTCHA_FAILED: "botCheckFailed",
  NETWORK_UNREACHABLE: "offline",
} as const satisfies Record<ErrorCode, ErrorClass>;

// One American-English sentence per class — plain, warm, direct. The UI
// never renders `message` raw; it maps `code`
// through `errorCodeClass` to this table via `showApiError()` / `ErrorModalHost`
// (owned by @sportsmet/ui). Never inline a second copy of one of these sentences
// at a call site.
export const errorClassCopy: Record<ErrorClass, string> = {
  notFound: "We couldn't find that.",
  validation: "That doesn't look right. Check the info and try again.",
  forbidden: "You don't have access to do that.",
  conflict: "That's already been done, or someone beat you to it.",
  lockedOut: "This is locked in and can't be changed anymore.",
  entitlementRequired: "You'll need to upgrade to do that.",
  rateLimited: "Slow down a little and try again in a moment.",
  providerUnavailable: "That's unavailable right now. Try again shortly.",
  internal: "Something went wrong on our end. Try again in a moment.",
  // Says what happened and what to do next. Refreshing mints a fresh
  // reCAPTCHA v3 token, because a token expires after 120 seconds. Uses no
  // tech word ("captcha", "token", "reCAPTCHA") a reader would not use.
  botCheckFailed:
    "We couldn't confirm you're a person. Refresh the page and try again.",
  // Names the connection as the problem and gives the next step. The SDK call
  // helper is the only producer of this class.
  offline:
    "We could not reach the server. Check your connection and try again.",
};

// The HTTP status every error in a class answers with. `registerErrorHandler`
// (`@sportsmet/api-core`) uses this so a thrown `ApiError` never has to name a
// status code by hand — the class picks it, once, here.
export const errorClassHttpStatus: Record<ErrorClass, number> = {
  notFound: 404,
  validation: 400,
  forbidden: 403,
  conflict: 409,
  lockedOut: 423,
  entitlementRequired: 402,
  rateLimited: 429,
  providerUnavailable: 503,
  internal: 500,
  botCheckFailed: 403,
  // No service ever answers with this class. The entry exists because the
  // table is exhaustive over `ErrorClass`.
  offline: 503,
};

export function resolveHttpStatus(code: ErrorCode): number {
  return errorClassHttpStatus[errorCodeClass[code]];
}

export function resolveClassCopy(code: ErrorCode): string {
  return errorClassCopy[errorCodeClass[code]];
}
